import React, { FC, useEffect, useRef, useState } from "react";
import { Button, Spin, Typography, message } from "antd";
import {
  ExportOutlined,
  DesktopOutlined,
  ExpandOutlined,
} from "@ant-design/icons";
import { TpaPortalConfig } from "../../../hooks/useTpaPortalConfig";
import { isLikelyEmbedBlocked } from "../../../utils/tpaPortalUrls";
import {
  focusTpaPortalPopup,
  openTpaPortalPopup,
} from "../../../utils/openTpaPortalPopup";
import "./PortalWebView.css";

type PortalWebViewProps = {
  config: TpaPortalConfig | null;
  loading?: boolean;
};

const EMBED_TIMEOUT_MS = 5000;

const PortalWebView: FC<PortalWebViewProps> = ({ config, loading }) => {
  const [forceEmbed, setForceEmbed] = useState(false);
  const [embedBlocked, setEmbedBlocked] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const iframeLoadedRef = useRef(false);
  const loadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popupRef = useRef<Window | null>(null);
  const popupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const skipEmbedByDefault =
    !!config?.portalUrl && isLikelyEmbedBlocked(config.portalUrl);
  const showIframe = forceEmbed && !embedBlocked;

  const clearPopupPoll = () => {
    if (popupPollRef.current) {
      clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
  };

  const watchPopup = (popup: Window) => {
    clearPopupPoll();
    popupPollRef.current = setInterval(() => {
      if (!popup || popup.closed) {
        clearPopupPoll();
        popupRef.current = null;
        setPopupOpen(false);
      }
    }, 800);
  };

  useEffect(() => {
    setForceEmbed(false);
    setEmbedBlocked(false);
    iframeLoadedRef.current = false;
  }, [config?.portalUrl]);

  useEffect(() => {
    if (!config?.portalUrl || !showIframe) return;

    iframeLoadedRef.current = false;
    setEmbedBlocked(false);

    if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    loadTimerRef.current = setTimeout(() => {
      if (!iframeLoadedRef.current) {
        setEmbedBlocked(true);
      }
    }, EMBED_TIMEOUT_MS);

    return () => {
      if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    };
  }, [config?.portalUrl, showIframe]);

  useEffect(() => () => clearPopupPoll(), []);

  const openControlledPopup = () => {
    if (!config?.portalUrl) return;

    if (popupRef.current && !popupRef.current.closed) {
      focusTpaPortalPopup(popupRef.current);
      setPopupOpen(true);
      message.info("Portal window is already open — focused it.");
      return;
    }

    const { popup, blocked } = openTpaPortalPopup(config.portalUrl);
    if (blocked || !popup) {
      message.warning(
        "Popup blocked by the browser. Allow popups for GSeven, then try again."
      );
      setPopupOpen(false);
      return;
    }

    popupRef.current = popup;
    setPopupOpen(true);
    watchPopup(popup);
  };

  const openInNewTab = () => {
    if (!config?.portalUrl) return;
    window.open(config.portalUrl, "_blank", "noopener,noreferrer");
  };

  if (loading || !config) {
    return (
      <div className="tpa-portal-webview" style={{ padding: 48, textAlign: "center" }}>
        <Spin tip="Loading portal configuration…" />
      </div>
    );
  }

  const { patientContext } = config;
  const showLaunchPanel = !showIframe;

  return (
    <div className="tpa-portal-webview">
      <div className="tpa-portal-chrome">
        <div className="tpa-portal-url-bar" title={config.portalUrl}>
          {config.portalUrl}
        </div>
        <div className="tpa-portal-chrome-actions">
          <span className={`tpa-portal-badge${popupOpen ? " tpa-portal-badge-live" : ""}`}>
            {popupOpen ? "POPUP OPEN" : "EXTERNAL PORTAL"}
          </span>
          <Button
            size="small"
            type="primary"
            icon={<DesktopOutlined />}
            onClick={openControlledPopup}
          >
            {popupOpen ? "Focus portal" : "Open portal window"}
          </Button>
        </div>
      </div>

      <div className="tpa-portal-context-bar">
        <span>
          Patient <strong>{patientContext.patientName}</strong>
        </span>
        <span>
          UHID <strong>{patientContext.uhid}</strong>
        </span>
        <span>
          CR No. <strong>{patientContext.crNo}</strong>
        </span>
        <span>
          DOA <strong>{patientContext.doa}</strong>
        </span>
        <span>
          Claim ID <strong>{patientContext.claimId || "—"}</strong>
        </span>
      </div>

      {(config.userName || config.usedFallbackUrl) && (
        <div className="tpa-portal-creds-hint">
          {config.userName && (
            <span>
              Saved Insurance Setup username: <strong>{config.userName}</strong>
              {" · "}
            </span>
          )}
          {config.usedFallbackUrl ? (
            <span>
              Using fallback portal URL — set LOGIN URL in Hospital Credentials
              for this scheme.
            </span>
          ) : (
            <span>
              Portal opens in a controlled popup (not an iframe) so insurer
              frame-blocking does not apply. Snap the window beside GSeven if
              you want a split view.
            </span>
          )}
        </div>
      )}

      <div className="tpa-portal-iframe-wrap">
        {showLaunchPanel ? (
          <div className="tpa-portal-iframe-fallback">
            <Typography.Title level={5} style={{ margin: 0 }}>
              {popupOpen
                ? "Portal window is open"
                : "Open portal in a controlled window"}
            </Typography.Title>
            <Typography.Paragraph
              type="secondary"
              style={{ margin: 0, maxWidth: 520 }}
            >
              {skipEmbedByDefault
                ? "IHX and most insurer sites block iframes. A controlled popup uses a top-level window (same as a new tab) and avoids those checks. Place it beside GSeven for a split-screen workflow."
                : "Use a controlled portal window for the best experience. Embed may still fail on some sites."}
            </Typography.Paragraph>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Button
                type="primary"
                size="large"
                icon={<DesktopOutlined />}
                onClick={openControlledPopup}
              >
                {popupOpen ? "Focus portal window" : "Open portal window"}
              </Button>
              <Button icon={<ExportOutlined />} onClick={openInNewTab}>
                Open in new tab
              </Button>
              {!skipEmbedByDefault && (
                <Button
                  icon={<ExpandOutlined />}
                  onClick={() => {
                    iframeLoadedRef.current = false;
                    setEmbedBlocked(false);
                    setForceEmbed(true);
                  }}
                >
                  Try embed
                </Button>
              )}
            </div>
          </div>
        ) : (
          <iframe
            className="tpa-portal-iframe"
            title={`${config.portalName} provider portal`}
            src={config.portalUrl}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer"
            onLoad={() => {
              iframeLoadedRef.current = true;
              if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
            }}
            onError={() => setEmbedBlocked(true)}
          />
        )}
      </div>
    </div>
  );
};

export default PortalWebView;
